import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './ProfileDetail.module.css';
import { getProfileByDocumentId } from '../services/skillProfileService';
import TagBadge from '../components/TagBadge';
import SocialLinks from '../components/SocialLinks';

const ProfileDetail = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchProfile = async () => {
      setLoading(true);
      setError(null);
      setNotFound(false);

      try {
        const data = await getProfileByDocumentId(documentId);
        if (!isMounted) return;

        if (!data) {
          setNotFound(true);
        } else {
          setProfile(data);
        }
      } catch (err) {
        if (!isMounted) return;
        setError('Errore nel caricamento del profilo.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchProfile();

    return () => {
      isMounted = false;
    };
  }, [documentId]);

  const professions = useMemo(
    () => (profile?.tags || []).filter((t) => t.category === 'profession'),
    [profile]
  );
  const hobbies = useMemo(
    () => (profile?.tags || []).filter((t) => t.category === 'hobby'),
    [profile]
  );
  const skills = useMemo(
    () => (profile?.tags || []).filter((t) => t.category === 'skill'),
    [profile]
  );

  if (loading) {
    return (
      <div className={styles.page}>
        <p>Caricamento profilo…</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className={styles.page}>
        <p className={styles.error}>Profilo non trovato.</p>
        <div className={styles.backLinkWrap}>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate('/network')}
          >
            ← Torna al network
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.page}>
        <p className={styles.error}>{error}</p>
        <div className={styles.backLinkWrap}>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate('/network')}
          >
            ← Torna al network
          </button>
        </div>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  const {
    fullName,
    bio,
    profileImageUrl,
    email,
    phone,
    website,
    instagram,
    facebook,
    tiktok,
    twitch,
    discord,
  } = profile;

  return (
    <div className={styles.page}>
      <div className={styles.backLinkWrap}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate('/network')}
        >
          ← Torna al network
        </button>
      </div>

      <div className={styles.card}>
        {profileImageUrl && (
          <div className={styles.imageWrapper}>
            <img
              src={profileImageUrl}
              alt={fullName || 'Profilo'}
              className={styles.image}
            />
          </div>
        )}

        <div className={styles.body}>
          <header className={styles.header}>
            <h1 className={styles.name}>{fullName}</h1>
          </header>

          {bio && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Chi sono</h2>
              <p className={styles.text}>{bio}</p>
            </section>
          )}

          {professions.length > 0 && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Professioni</h2>
              <div className={styles.tagsRow}>
                {professions.map((tag) => (
                  <TagBadge
                    key={tag.id}
                    label={tag.name}
                    category={tag.category}
                  />
                ))}
              </div>
            </section>
          )}

          {hobbies.length > 0 && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Hobby</h2>
              <div className={styles.tagsRow}>
                {hobbies.map((tag) => (
                  <TagBadge
                    key={tag.id}
                    label={tag.name}
                    category={tag.category}
                  />
                ))}
              </div>
            </section>
          )}

          {skills.length > 0 && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Competenze</h2>
              <div className={styles.tagsRow}>
                {skills.map((tag) => (
                  <TagBadge
                    key={tag.id}
                    label={tag.name}
                    category={tag.category}
                  />
                ))}
              </div>
            </section>
          )}

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Contatti</h2>
            <SocialLinks
              website={website}
              email={email}
              phone={phone}
              instagram={instagram}
              facebook={facebook}
              tiktok={tiktok}
              twitch={twitch}
              discord={discord}
            />
          </section>
        </div>
      </div>
    </div>
  );
};

export default ProfileDetail;
